/**
 * CreateUserDialog — create an SSO user from the User management page.
 * The user is provisioned in Keycloak and added to the chosen account's
 * group (POST /account/{id}/sso-users, admin 1.0.26), which is what grants
 * access to the customer portal and the contact center of that account.
 */

import React, { useEffect, useState } from "react";
import { useNotify, useTranslate } from "react-admin";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  TextField,
  Typography,
} from "@mui/material";
import { extractErrorMessage } from "@/utils/extractErrorMessage";
import { createAccountSsoUser } from "@/features/accounts/services/accountService";
import { getAccountsList, AccountOption } from "@/features/manage-groups/services/groupService";
import { useAccountLookup } from "../../accounts/hooks/useAccountLookup";

const i18nBase = "resources.user-management";

export interface CreateUserDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const CreateUserDialog: React.FC<CreateUserDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  // Los permisos que existen, servidos por el backend: esta pantalla no los mantiene.
  // Aqui se ofrecen todos porque el alta no cuelga de una cuenta concreta y no hay
  // productos contratados con los que cruzarlos; el servidor recorta lo que no aplique.
  const { data: scopeCatalog = [] } = useAccountLookup("scopeCatalog");
  const [accounts, setAccounts] = useState<AccountOption[]>([]);
  const [accountIds, setAccountIds] = useState<string[]>([]);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [telegramChatId, setTelegramChatId] = useState("");
  // Se arranca con lo minimo util. Marcar las seis por defecto repartia «gestionar
  // usuarios» a todo el que se diera de alta desde aqui, que es justo el permiso que
  // deja a un cliente administrar el equipo de su hotel.
  const [scopes, setScopes] = useState<string[]>(["portal"]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setAccountIds([]);
    setEmail("");
    setName("");
    setSurname("");
    setPassword("");
    setPhone("");
    setTelegramChatId("");
    setScopes(["portal"]);
    void getAccountsList().then(setAccounts);
  }, [open]);

  const create = async (): Promise<void> => {
    setBusy(true);
    try {
      // Same endpoint per account: the first call provisions the identity,
      // the rest reuse it by email and just add the group membership.
      for (const accountId of accountIds) {
        await createAccountSsoUser(accountId, {
          email: email.trim(),
          name: name.trim() || undefined,
          surname: surname.trim() || undefined,
          password: password || undefined,
          phone: phone.trim() || undefined,
          telegramChatId: telegramChatId.trim() || undefined,
          // Permisos POR CUENTA: este mismo endpoint escribe la fila de cada cuenta
          // seleccionada. El booleano antiguo de la base de conocimientos lo sustituye
          // el scope «knowledge-base», asi que no se manda.
          scopes,
          canUseContactCenterAi: false,
        });
      }
      notify("resources.accounts.usersDialog.created", { type: "success" });
      onSuccess();
    } catch (err: unknown) {
      notify(
        extractErrorMessage(err, "ra.notification.http_error", translate),
        { type: "error" },
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !busy && onClose()} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontWeight: 700 }}>
        {translate(`${i18nBase}.actions.addUser`)}
      </DialogTitle>
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}
      >
        <Typography sx={{ fontSize: 12, fontWeight: 600, mb: -1 }}>
          {translate(`${i18nBase}.fields.accounts`)}
        </Typography>
        <FormGroup
          sx={{
            maxHeight: 180,
            overflowY: "auto",
            flexWrap: "nowrap",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            px: 1.5,
            py: 0.5,
          }}
        >
          {accounts.map((a) => (
            <FormControlLabel
              key={a.id}
              control={
                <Checkbox
                  size="small"
                  checked={accountIds.includes(a.id)}
                  onChange={(_e, checked) =>
                    setAccountIds((prev) =>
                      checked ? [...prev, a.id] : prev.filter((x) => x !== a.id),
                    )
                  }
                />
              }
              label={a.name}
            />
          ))}
        </FormGroup>
        <TextField
          label={translate(`${i18nBase}.fields.email`)}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          size="small"
        />
        <TextField
          label={translate(`${i18nBase}.fields.name`)}
          value={name}
          onChange={(e) => setName(e.target.value)}
          size="small"
        />
        <TextField
          label={translate(`${i18nBase}.fields.surname`)}
          value={surname}
          onChange={(e) => setSurname(e.target.value)}
          size="small"
        />
        <TextField
          label={translate("resources.accounts.usersDialog.password")}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          size="small"
        />
        <TextField
          label={translate(`${i18nBase}.fields.phone`)}
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          size="small"
        />
        <TextField
          label={translate(`${i18nBase}.fields.telegramChatId`)}
          value={telegramChatId}
          onChange={(e) => setTelegramChatId(e.target.value)}
          size="small"
        />
        <Typography sx={{ fontSize: 12, fontWeight: 600, mb: -1 }}>
          {translate(`${i18nBase}.fields.scopes`)}
        </Typography>
        <FormGroup row>
          {scopeCatalog.map(({ code: sc }) => (
            <FormControlLabel
              key={sc}
              control={
                <Checkbox
                  size="small"
                  checked={scopes.includes(sc)}
                  onChange={(_e, checked) =>
                    setScopes((prev) =>
                      checked ? [...prev, sc] : prev.filter((x) => x !== sc),
                    )
                  }
                />
              }
              label={translate(`${i18nBase}.scopes.${sc}`)}
            />
          ))}
        </FormGroup>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={onClose}>
          {translate("ra.action.cancel")}
        </Button>
        <Button
          variant="contained"
          disabled={busy || accountIds.length === 0 || !email.trim().includes("@")}
          onClick={() => void create()}
        >
          {translate("resources.accounts.usersDialog.create")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateUserDialog;
